import { ReactNode, useMemo, useRef, useState } from "react";
import {
  DialogOverlay as ReachDialogOverlay,
  DialogContent as ReachDialogContent,
} from "@reach/dialog";
import {
  Box,
  Button,
  Flex,
  Icon,
  Play,
  SystemProps,
  Text,
  css,
} from "@storyofams/react-ui";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import ReactPlayer from "react-player";
import styled from "styled-components";
import SwiperCore from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { FixedRatio } from "~components/common/FixedRatio";
import { Cross, PlayButton } from "~components/common/Icons";
import { Slider } from "~components/common/Slider";
import { GallerySlider } from "~components/common/Slider/GallerySlider";
const MotionOverlay = motion(ReachDialogOverlay);
const MotionDialog = motion(ReachDialogContent);

const DialogOverlay = styled(MotionOverlay)<SystemProps>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  z-index: ${(p) => p.theme.zIndices.modal};

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(0, 0, 0, 0.6);
  opacity: 0.75;
  @media (max-width: ${(p) => p.theme.breakpoints.md}) {
    padding: ${(p) => p.theme.space[4]}px ${(p) => p.theme.space[2]}px 92px;
  }
`;

const DialogContent = styled(MotionDialog)<{ children?: ReactNode }>`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 100%;
  background: transparent;
  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    max-width: 774px;
  }
`;

const CloseButton = styled(Button)`
  position: absolute;
  right: 16px;
  top: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 100px;
  transition: color 0.2s;
  z-index: 2;
`;

const ContentWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 0 auto;
  padding: 40px 16px;
  width: max-content;
  height: max-content;
`;

export const Media = ({ media }) => {
  const content = useMemo(() => {
    return media.map((item) =>
      item.includes("images") ||
      item.includes(".jpeg") ||
      item.includes(".png") ||
      item.includes("width=") ||
      item.includes(".jpg") ||
      item.includes(".gif") ||
      item.includes(".null")
        ? { src: item, type: "image" }
        : { src: item, type: "video" }
    );
  }, []);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const swiperRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  return (
    <>
      <Box
        width={"100%"}
        position={"relative"}
        overflow={"clip"}
        display={"flow-root"}
        css={{ "overflow-clip-margin": "content-box 10px;" }}
      >
        <Box position={"relative"}>
          <Swiper
            onInit={(core: SwiperCore) => {
              swiperRef.current = core.el;
            }}
            slidesPerView={1}
            spaceBetween={24}
            mousewheel={false}
            className="product-slider"
            onSlideChange={(e) => {
              setActiveIndex(e.activeIndex);
              setIsPlaying(false);
            }}
          >
            {content?.map(({ type, src }, i) => (
              <SwiperSlide key={src + i} onClick={() => setIsOpen(true)}>
                <FixedRatio
                  ratio={[[2, 1.75]]}
                  borderRadius={"md"}
                  overflow={"hidden"}
                  css={css({
                    video: {
                      borderRadius: "md",
                    },
                    img: {
                      borderRadius: "md",
                    },
                  })}
                >
                  {type === "image" ? (
                    <Image
                      src={src}
                      layout="fill"
                      objectFit="cover"
                      objectPosition="center"
                      priority
                      blurDataURL={content?.length && content[i].src}
                      placeholder={content?.length ? "blur" : "empty"}
                    />
                  ) : src.includes("youtu") ? (
                    <>
                      <Box
                        width={"100%"}
                        height={"100%"}
                        display={isPlaying ? "block" : "none"}
                      >
                        <ReactPlayer
                          loop={true}
                          playsinline
                          playing={isPlaying}
                          width={"100%"}
                          onPause={() => setIsPlaying(false)}
                          style={{
                            backgroundImage: "none",
                            objectFit: "cover",
                          }}
                          on
                          height={"100%"}
                          stopOnUnmount={true}
                          url={src}
                        />
                      </Box>
                      <ReactPlayer
                        url={src}
                        playing={false}
                        width={"100%"}
                        height={"100%"}
                      />
                      <Box
                        width={"47px"}
                        top={8}
                        left={"27px"}
                        pl="15px"
                        pt="7px"
                        borderRadius={"4px"}
                        height={"35px"}
                        bg="white"
                        position={"absolute"}
                        cursor={"pointer"}
                      >
                        <Icon icon={PlayButton} fontSize={"20px"} />
                      </Box>
                    </>
                  ) : (
                    <ReactPlayer
                      loop={true}
                      width={"100%"}
                      style={{
                        backgroundImage: "none",
                        objectFit: "cover",
                      }}
                      muted
                      on
                      playing
                      height={"100%"}
                      stopOnUnmount={true}
                      url={src}
                      playsinline
                    />
                  )}
                </FixedRatio>
              </SwiperSlide>
            ))}
          </Swiper>
          <Flex
            zIndex={5}
            bottom={2}
            width={"100%"}
            justifyContent={"center"}
            position={"absolute"}
          >
            {content.map(({ src }, i) => (
              <Box
                borderRadius={"full"}
                key={src + i}
                mr={1}
                height={"10px"}
                width={"10px"}
                bg={activeIndex === i ? "white" : "grey400"}
              ></Box>
            ))}
          </Flex>
        </Box>
        <GallerySlider slidesPerView={4}>
          {content.map(({ type, src }, i) => (
            <FixedRatio
              ratio={[[1, 1]]}
              key={src + i}
              cursor={"pointer"}
              mt={1}
              width={"100%"}
              overflow={"hidden"}
              borderRadius={"lg"}
              border={i === activeIndex ? "1px solid black" : "none"}
              position={"relative"}
              css={css({
                video: {
                  borderRadius: "md",
                  objectFit: "cover",
                },
                img: {
                  borderRadius: "md",
                },
              })}
            >
              {type === "image" ? (
                <Image
                  onClick={() => {
                    swiperRef.current?.swiper.slideTo(i);
                  }}
                  src={src}
                  objectFit="cover"
                  layout="fill"
                />
              ) : (
                <Box
                  borderRadius={"8px"}
                  width={"100%"}
                  height={"100%"}
                  onClick={() => {
                    swiperRef.current?.swiper.slideTo(i);
                  }}
                >
                  <ReactPlayer
                    url={src}
                    position={"relative"}
                    playing={false}
                    width={"100%"}
                    height={"100%"}
                  />

                  <Icon
                    width={"100%"}
                    mx="auto"
                    left={0}
                    right={0}
                    top={["30%", "40%"]}
                    position={"absolute"}
                    icon={Play}
                    ml={"3px"}
                    color={"white"}
                    fontSize={["28px", "36px"]}
                  />
                </Box>
              )}
            </FixedRatio>
          ))}
        </GallerySlider>
      </Box>
      <AnimatePresence>
        {isOpen && (
          <DialogOverlay
            onDismiss={() => setIsOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <DialogContent
              aria-label={"image-modal"}
              initial={{ y: -10 }}
              animate={{ y: 0 }}
              exit={{ y: -10 }}
            >
              <ContentWrapper
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 1 }}
              >
                <CloseButton
                  variant="unstyled"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close modal"
                >
                  <Icon icon={Cross} fontSize={2.5} color="grey900" />
                </CloseButton>

                <Box
                  width={"800px"}
                  bg="white"
                  className="slider-container"
                  height={["300px", "800px"]}
                  maxHeight={["calc(100vh - 80px)", "100%"]}
                  maxWidth={["calc(100vw - 32px)", "100%"]}
                  overflow="hidden"
                >
                  <Slider slidesPerView={1} initialSlide={activeIndex}>
                    {content.map(({ src, type }) => (
                      <Box
                        height={["300px", "800px"]}
                        maxHeight={["calc(100vh - 80px)", "100%"]}
                        maxWidth={["calc(100vw - 32px)", "100%"]}
                        key={src}
                        position={"relative"}
                      >
                        {type === "video" ? (
                          <ReactPlayer
                            url={src}
                            height={"100%"}
                            width={"100%"}
                            playing
                            playsinline
                            muted
                            controls
                            config={{
                              youtube: {
                                playerVars: {
                                  controls: true,
                                },
                              },
                            }}
                          />
                        ) : (
                          <Image
                            src={src}
                            objectFit="cover"
                            layout="fill"
                          ></Image>
                        )}
                      </Box>
                    ))}
                  </Slider>
                </Box>
              </ContentWrapper>
            </DialogContent>
          </DialogOverlay>
        )}
      </AnimatePresence>
    </>
  );
};
